<template>
    <div class="history-row text-xs">
        <div class="icon-column">
            <div class="icon-circle flex items-center justify-center rounded-full">
                <Icon :width="currentIcon.size" :height="currentIcon.size" :vbSize="currentIcon.viewBox">
                    <component :is="currentIcon.component" />
                </Icon>
            </div>
        </div>
        <div class="content-column flex flex-col gap-1 min-w-0">
            <div class="title-row">
                <span class="font-semibold">{{ actionLabel }}</span>
                <span v-if="formattedDateTime" class="text-gray">{{ formattedDateTime }}</span>
            </div>
            <div v-if="messageText" class="text-gray">{{ messageText }}</div>
            <div v-if="eventDetails.length" class="text-gray flex flex-col gap-1">
                <div v-for="(detail, index) in eventDetails" :key="index">{{ detail }}</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Icon from '@/ui/Icon.vue';
import IconCheckin from '@/ui/icons/IconCheckin.vue';
import IconCheckout from '@/ui/icons/IconCheckout.vue';
import IconClose from '@/ui/icons/IconClose.vue';
import IconAdd from '@/ui/icons/IconAdd.vue';
import IconEdit from '@/ui/icons/IconEdit.vue';

dayjs.extend(utc);

interface Props {
    record: any
    previousRecord?: any
}

const { record, previousRecord } = defineProps<Props>()

const normalizedAction = computed(() => {
    if (record?.action) return String(record.action).toUpperCase()
    if (record?.history_type === '+') return 'CREATED'
    return null
})

const actionLabels: Record<string, string> = {
    CREATED: 'Смена создана',
    UPDATED: 'Смена изменена',
    DELETED: 'Смена удалена',
    CHECKIN: 'Отметка на приход',
    CHECKOUT: 'Отметка на уход',
    FACT_UPDATED: 'Фактическое время изменено',
}

interface IconConfig {
    component: object
    size: number
    viewBox?: number
}

const iconMap: Record<string, IconConfig> = {
    CREATED: { component: IconAdd, size: 14, viewBox: 14 },
    UPDATED: { component: IconEdit, size: 14, viewBox: 18 },
    DELETED: { component: IconClose, size: 16, viewBox: 24 },
    CHECKIN: { component: IconCheckin, size: 16, viewBox: 20 },
    CHECKOUT: { component: IconCheckout, size: 16, viewBox: 20 },
    FACT_UPDATED: { component: IconEdit, size: 14, viewBox: 18 },
}

const fallbackIcon: IconConfig = { component: IconEdit, size: 14, viewBox: 18 }

const actionLabel = computed(() => {
    const action = normalizedAction.value
    if (action && actionLabels[action]) return actionLabels[action]

    return record?.message || action || 'Изменение смены'
})

const currentIcon = computed<IconConfig>(() => {
    const action = normalizedAction.value
    if (!action) return fallbackIcon

    return iconMap[action] || fallbackIcon
})

const createdAt = computed(() => record?.created_at || record?.history_date || null)

const formattedDateTime = computed(() => {
    if (!createdAt.value) return null

    return dayjs.utc(createdAt.value).local().format('DD.MM.YYYY HH:mm')
})

const messageText = computed(() => record?.message || null)

const formatTime = (value: string | null | undefined) => {
    if (!value) return null

    return dayjs(value).format('HH:mm')
}

const valueOrDash = (value: string | null | undefined) => value || '—'

const formatTimeOrDash = (value: string | null | undefined) => {
    const formatted = formatTime(value)
    return valueOrDash(formatted)
}

const isSameTime = (left: string | null | undefined, right: string | null | undefined) => {
    const leftTime = formatTime(left)
    const rightTime = formatTime(right)
    if (!leftTime && !rightTime) return true
    return leftTime === rightTime
}

const formatFactFallback = (source: any) => {
    const start = formatTime(source?.fact_work_start_at)
    const end = formatTime(source?.fact_work_end_at)

    if (start && end) return `Факт: ${start} — ${end}`
    if (start) return `Факт прихода: ${start}`
    if (end) return `Факт ухода: ${end}`
    return null
}

const getPositionCompareValue = (source: any) => source?.position?.id || source?.position?.name || null
const getPositionDisplayValue = (source: any) => source?.position?.name || source?.position?.id || null

const formatOvertime = (value: any) => value === true ? 'Да' : 'Нет'

const getComparedFieldDetails = (
    current: any,
    previous: any,
    options: { includeFactFields?: boolean; includeCommonFields?: boolean } = {},
) => {
    if (!previous) return []
    const { includeFactFields = false, includeCommonFields = true } = options

    const details: string[] = []

    if (!isSameTime(current?.plan_work_start_at, previous?.plan_work_start_at)) {
        details.push(`План начало: ${formatTimeOrDash(previous?.plan_work_start_at)} → ${formatTimeOrDash(current?.plan_work_start_at)}`)
    }

    if (!isSameTime(current?.plan_work_end_at, previous?.plan_work_end_at)) {
        details.push(`План окончание: ${formatTimeOrDash(previous?.plan_work_end_at)} → ${formatTimeOrDash(current?.plan_work_end_at)}`)
    }

    if (includeFactFields) {
        if (!isSameTime(current?.fact_work_start_at, previous?.fact_work_start_at)) {
            details.push(`Факт приход: ${formatTimeOrDash(previous?.fact_work_start_at)} → ${formatTimeOrDash(current?.fact_work_start_at)}`)
        }

        if (!isSameTime(current?.fact_work_end_at, previous?.fact_work_end_at)) {
            details.push(`Факт уход: ${formatTimeOrDash(previous?.fact_work_end_at)} → ${formatTimeOrDash(current?.fact_work_end_at)}`)
        }
    }

    if (includeCommonFields) {
        const currentDayType = current?.day_type_code || null
        const previousDayType = previous?.day_type_code || null
        if (currentDayType !== previousDayType) {
            details.push(`Тип дня: ${valueOrDash(previousDayType || undefined)} → ${valueOrDash(currentDayType || undefined)}`)
        }

        const currentPositionCompare = getPositionCompareValue(current)
        const previousPositionCompare = getPositionCompareValue(previous)
        if (currentPositionCompare !== previousPositionCompare) {
            details.push(`Должность: ${valueOrDash(getPositionDisplayValue(previous) || undefined)} → ${valueOrDash(getPositionDisplayValue(current) || undefined)}`)
        }

        const currentOvertime = current?.is_overtime
        const previousOvertime = previous?.is_overtime
        if (currentOvertime !== previousOvertime && currentOvertime !== undefined && previousOvertime !== undefined) {
            details.push(`Доп. смена: ${formatOvertime(previousOvertime)} → ${formatOvertime(currentOvertime)}`)
        }
    }

    return details
}

const getUpdatedDetails = () => getComparedFieldDetails(record, previousRecord, { includeFactFields: false, includeCommonFields: true })
const getFactUpdatedDetails = () => getComparedFieldDetails(record, previousRecord, { includeFactFields: true, includeCommonFields: false })

const eventDetails = computed(() => {
    const action = normalizedAction.value
    if (!action) return []

    if (action === 'CHECKIN') {
        const start = formatTime(record?.fact_work_start_at)
        return start ? [`Факт прихода: ${start}`] : []
    }

    if (action === 'CHECKOUT') {
        const end = formatTime(record?.fact_work_end_at)
        return end ? [`Факт ухода: ${end}`] : []
    }

    if (action === 'FACT_UPDATED') {
        const compared = getFactUpdatedDetails()
        if (compared.length) return compared

        const fact = formatFactFallback(record)
        return fact ? [fact] : []
    }

    if (action === 'UPDATED') {
        return getUpdatedDetails()
    }

    return []
})
</script>

<style scoped>
.history-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.icon-column {
    width: 32px;
    min-width: 32px;
    flex: 0 0 32px;
    padding-top: 2px;
}

.icon-circle {
    width: 32px;
    height: 32px;
    background-color: #f0f0f0;
    color: #606060;
}

.content-column {
    flex: 1;
    min-width: 0;
}

.title-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
}
</style>
